"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import styles from "./SunlitBackground.module.css";
import { useSunlitTheme } from "./useSunlitTheme";

const SHUTTERS = Array.from({ length: 23 }, (_, index) => index);

function isTypingTarget(target) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    target.isContentEditable ||
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT"
  );
}

const SUPPORTED_PATHS = new Set(["/", "/blog", "/project", "/message"]);

function supportsSunlitKeyboard(pathname) {
  return SUPPORTED_PATHS.has(pathname) || pathname.startsWith("/blog/");
}

export default function SunlitBackground() {
  const pathname = usePathname();
  const supportsKeyboard = supportsSunlitKeyboard(pathname);
  const isArticle = pathname.startsWith("/blog/");
  const { toggleTheme } = useSunlitTheme();

  useEffect(() => {
    if (!supportsKeyboard) {
      return;
    }

    const handleKeyDown = (event) => {
      if (
        event.code !== "Space" ||
        event.repeat ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      toggleTheme({ animate: true });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [supportsKeyboard, toggleTheme]);

  return (
    <div className={styles.root} aria-hidden="true">
      <div className={styles.backdrop} />
      <div className={styles.glow} />
      <div className={styles.glowBounce} />
      <div className={styles.perspective}>
        <div className={styles.leaves} />
        <div className={styles.blinds}>
          <div className={styles.shutters}>
            {SHUTTERS.map((shutter) => (
              <div key={shutter} className={styles.shutter} />
            ))}
          </div>
          <div className={styles.vertical}>
            <div className={styles.bar} />
            <div className={styles.bar} />
          </div>
        </div>
      </div>
      <div className={styles.sideFade} />
      <div
        className={`${styles.readabilityVeil} ${
          isArticle ? styles.readabilityVeilArticle : ""
        }`}
      />
    </div>
  );
}
